import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

interface InboxSelectorProps {
  selectedInbox: string;
  onSelectInbox: (inboxId: string) => void;
}

interface EmailAccount {
  id: string;
  email: string;
  provider: string;
  name: string;
}

const InboxSelector = ({ selectedInbox, onSelectInbox }: InboxSelectorProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['emailAccounts'],
    queryFn: async () => {
      console.log('Fetching email accounts...');
      const { data, error } = await supabase
        .from('email_accounts')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching email accounts:', error);
        throw error;
      }

      console.log('Email accounts fetched:', data);
      return data as EmailAccount[];
    },
  });

  // Automatically select the first account when accounts are loaded
  useEffect(() => {
    if (accounts.length > 0 && !selectedInbox) {
      const firstAccount = accounts[0];
      console.log('Auto-selecting first account:', firstAccount.email);
      console.log('Current selected inbox:', selectedInbox);
      console.log('Available accounts:', accounts);
      
      // Clear any existing email data before selecting new inbox
      queryClient.removeQueries({ queryKey: ['emails'] });
      onSelectInbox(firstAccount.email);
    }
  }, [accounts, selectedInbox, onSelectInbox, queryClient]);

  const handleSelectInbox = (email: string) => {
    console.log('Selecting inbox:', email);
    console.log('Current selected inbox:', selectedInbox);
    
    if (email !== selectedInbox) {
      console.log('Switching to new inbox:', email);
      // Clear any existing email data before selecting new inbox
      queryClient.removeQueries({ queryKey: ['emails'] });
      onSelectInbox(email);
    } else {
      console.log('Same inbox selected, no action needed');
    }
  };

  const handleReconnectSuccess = () => {
    console.log("Gmail reconnected successfully");
    // Invalidate both the email accounts query and the emails query
    queryClient.invalidateQueries({ queryKey: ['emailAccounts'] });
    queryClient.invalidateQueries({ queryKey: ['emails'] });
    toast({
      title: "Success",
      description: "Gmail account reconnected successfully",
    });
  };

  if (isLoading) {
    return <div className="p-4">Loading accounts...</div>;
  }

  if (accounts.length === 0) {
    return (
      <div className="space-y-2">
        <h2 className="font-semibold px-2">Connected Accounts</h2>
        <div className="p-4 text-center text-sm text-muted-foreground">
          No email accounts connected. Add one in Settings.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h2 className="font-semibold px-2">Connected Accounts</h2>
      <div className="space-y-1">
        {accounts.map((account) => (
          <Button
            key={account.id}
            variant={selectedInbox === account.email ? "secondary" : "ghost"}
            className="w-full justify-start gap-2 min-h-[44px] py-2 px-2"
            onClick={() => handleSelectInbox(account.email)}
          >
            <Mail className="h-4 w-4 text-blue-500" />
            <div className="flex-1 text-left max-w-[calc(100%-2rem)]">
              <span className="font-medium line-clamp-2 text-sm break-all">
                {account.name}
              </span>
            </div>
          </Button>
        ))}
      </div>
    </div>
  );
};

export default InboxSelector;